// src/components/Signup.jsx（或 src/pages/Signup.jsx）
import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Mail, Lock, ArrowRight } from "lucide-react";

import {
  createUserWithEmailAndPassword,
  setPersistence,
  browserSessionPersistence,
} from "firebase/auth";
import { auth } from "../firebase"; // 如果这个文件在 src/components，路径就是这样

export default function Signup() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");

  const handleSignup = async (e) => {
    e.preventDefault();
    setError("");

    // ⭐ 简单前端校验
    if (password.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }

    if (password !== confirmPassword) {
      setError("Passwords do not match, please check again.");
      return;
    }

    try {
      // 只在当前浏览器会话内保存登录状态（关浏览器会自动登出）
      await setPersistence(auth, browserSessionPersistence);

      // Firebase 注册账号 + 自动登录
      await createUserWithEmailAndPassword(auth, email, password);

      // 注册成功后，直接进 dashboard（你也可以改成 navigate("/login")）
      navigate("/dashboard");
    } catch (err) {
      console.error(err);
      // 简单处理一下 firebase 报错
      setError(err.message || "Failed to sign up, please try again.");
    }
  };

  return (
    <div className="min-h-screen flex flex-col md:flex-row bg-gradient-to-br from-purple-50 via-white to-purple-100">
      {/* 左侧注册表单 */}
      <div className="w-full md:w-1/2 flex items-center justify-center p-10">
        <div className="bg-white/80 backdrop-blur-xl rounded-3xl shadow-[0_8px_40px_rgba(138,79,255,0.1)] w-full max-w-md p-10">
          <h2 className="text-3xl font-extrabold text-purple-500 mb-8">
            Create your account
          </h2>

          <form onSubmit={handleSignup} className="space-y-5">
            {/* Email */}
            <div className="relative">
              <Mail
                className="absolute left-4 top-3.5 text-purple-500"
                size={20}
              />
              <input
                type="email"
                placeholder="Email Address"
                className="w-full pl-11 pr-4 py-3 rounded-xl bg-purple-50/50 border border-purple-200 focus:outline-none focus:ring-2 focus:ring-purple-300 text-[#1e0b44] placeholder-purple-400"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>

            {/* Password */}
            <div className="relative">
              <Lock
                className="absolute left-4 top-3.5 text-purple-500"
                size={20}
              />
              <input
                type="password"
                placeholder="Password"
                className="w-full pl-11 pr-4 py-3 rounded-xl bg-purple-50/50 border border-purple-200 focus:outline-none focus:ring-2 focus:ring-purple-300 text-[#1e0b44] placeholder-purple-400"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>

            {/* Confirm Password */}
            <div className="relative">
              <Lock
                className="absolute left-4 top-3.5 text-purple-500"
                size={20}
              />
              <input
                type="password"
                placeholder="Confirm Password"
                className="w-full pl-11 pr-4 py-3 rounded-xl bg-purple-50/50 border border-purple-200 focus:outline-none focus:ring-2 focus:ring-purple-300 text-[#1e0b44] placeholder-purple-400"
                required
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
              />
            </div>

            {/* 错误提示 */}
            {error && <p className="text-xs text-red-500 mt-1">{error}</p>}

            {/* 注册按钮 */}
            <button
              type="submit"
              className="w-full flex justify-center items-center gap-2 bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 text-white py-3 rounded-xl font-semibold shadow-lg transition-all"
            >
              Sign up
              <ArrowRight size={18} />
            </button>

            <p className="text-xs text-center text-purple-500 mt-4">
              Having trouble signing up? Please contact{" "}
              <a
                href="mailto:support@ainova.com"
                className="text-purple-700 font-semibold"
              >
                support@ainova.com
              </a>
            </p>
          </form>

          <p className="text-sm text-center mt-8 text-purple-700">
            Already have an account?{" "}
            <a
              href="/login"
              className="text-purple-600 font-bold hover:text-purple-800"
            >
              Log in
            </a>
          </p>
        </div>
      </div>

      {/* 右侧品牌展示区（保持和 Login 一模一样） */}
      <div className="w-full md:w-1/2 bg-gradient-to-br from-purple-200 via-purple-100 to-white text-purple-900 flex flex-col justify-between relative overflow-hidden">
        <div className="absolute inset-0 opacity-20 bg-[radial-gradient(circle_at_30%_30%,_rgba(255,255,255,0.6),_transparent_70%)]"></div>

        {/* Contact Sales：手机端居中，桌面端在右边 */}
        <div className="flex justify-center md:justify-end p-6 relative z-10">
          <Link
            to="/contact"
            className="px-5 py-2 rounded-xl border border-white/60 bg-white/40 text-purple-700 font-semibold backdrop-blur-sm shadow-sm 
                 hover:bg-white/70 hover:border-purple-300 hover:text-purple-800 transition-all duration-200"
          >
            Contact Sales
          </Link>
        </div>

        <div className="flex flex-col items-center justify-center flex-1 text-center px-6 relative z-10">
          <img
            src="/ainova_logo_white.png"
            alt="AI NOVA"
            className="w-28 mb-4 mix-blend-multiply"
          />
          <p className="text-purple-600 mt-3 text-sm font-medium">
            Empowering intelligent compute infrastructure
          </p>
        </div>

        {/* 底部只保留 Privacy，并指向 /privacy */}
        <div className="flex justify-center pb-10 md:pb-14 pt-2 text-lg font-medium relative z-10">
          <Link
            to="/privacy"
            className="text-purple-700/80 hover:text-purple-900 hover:-translate-y-0.5 hover:font-semibold transition-all duration-200"
          >
            Privacy
          </Link>
        </div>
      </div>
    </div>
  );
}
